/**
 * MockupQuizSession — Full-screen interactive quiz runner for all quiz types.
 * Designed for children: colorful, animated, with storytelling elements.
 * Supports navigating through a list of quizzes (test session).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   STYLES — injected once
   ═══════════════════════════════════════════════════════════════════════════ */
let _injected = false;
function injectStyles() {
  if (_injected) return;
  _injected = true;
  const s = document.createElement('style');
  s.id = 'mockup-quiz-styles';
  s.textContent = `
    @keyframes mqPop      { 0%{opacity:0;transform:scale(.6)} 60%{transform:scale(1.08)} 100%{opacity:1;transform:scale(1)} }
    @keyframes mqBounce   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
    @keyframes mqShake    { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
    @keyframes mqFadeUp   { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
    @keyframes mqPulse    { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
    @keyframes mqConfetti { 0%{opacity:1;transform:translateY(0) rotate(0deg)} 100%{opacity:0;transform:translateY(-80px) rotate(720deg)} }
    @keyframes mqStarSpin { 0%{transform:rotate(0deg) scale(0)} 50%{transform:rotate(180deg) scale(1.3)} 100%{transform:rotate(360deg) scale(1)} }
    @keyframes mqSlideIn  { from{opacity:0;transform:translateX(40px)} to{opacity:1;transform:translateX(0)} }
    @keyframes mqGlow     { 0%,100%{box-shadow:0 0 8px rgba(255,215,0,.3)} 50%{box-shadow:0 0 24px rgba(255,215,0,.6)} }
    @keyframes mqFloat    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
    @keyframes mqRipple   { 0%{transform:scale(.5);opacity:1} 100%{transform:scale(3);opacity:0} }
    @keyframes mqTypewriter { from{width:0} to{width:100%} }

    .mq-pop      { animation: mqPop .5s cubic-bezier(.34,1.56,.64,1) both }
    .mq-bounce   { animation: mqBounce 1.2s ease-in-out infinite }
    .mq-shake    { animation: mqShake .5s ease both }
    .mq-fadeup   { animation: mqFadeUp .5s ease-out both }
    .mq-pulse    { animation: mqPulse 2s ease-in-out infinite }
    .mq-slidein  { animation: mqSlideIn .4s ease-out both }
    .mq-glow     { animation: mqGlow 2s ease-in-out infinite }
    .mq-float    { animation: mqFloat 3s ease-in-out infinite }

    .mq-option {
      transition: all .25s cubic-bezier(.4,0,.2,1);
      cursor: pointer;
      user-select: none;
    }
    .mq-option:hover:not(.mq-disabled) {
      transform: translateY(-4px) scale(1.02);
      box-shadow: 0 12px 28px rgba(99,102,241,.18);
    }
    .mq-option:active:not(.mq-disabled) { transform: translateY(0) scale(.97); }
    .mq-option.mq-disabled { cursor: default; opacity: .7; }
    .mq-option.mq-correct {
      border-color: #22c55e !important;
      background: linear-gradient(135deg, #f0fdf4, #dcfce7) !important;
      animation: mqPop .4s ease both;
    }
    .mq-option.mq-wrong {
      border-color: #ef4444 !important;
      background: linear-gradient(135deg, #fef2f2, #fecaca) !important;
      animation: mqShake .5s ease both;
    }
    .mq-option.mq-reveal-correct {
      border-color: #22c55e !important;
      background: #f0fdf4 !important;
      animation: mqPulse 1s ease-in-out 2;
    }

    .mq-progress-fill {
      transition: width .6s cubic-bezier(.4,0,.2,1);
    }

    .mq-input:focus {
      border-color: #818cf8 !important;
      box-shadow: 0 0 0 4px rgba(129,140,248,.2) !important;
      outline: none;
    }
  `;
  document.head.appendChild(s);
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONFETTI BURST (lightweight, no deps)
   ═══════════════════════════════════════════════════════════════════════════ */
const ConfettiBurst = ({ show }) => {
  if (!show) return null;
  const pieces = Array.from({ length: 24 }, (_, i) => {
    const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#A78BFA', '#FB923C', '#34D399'];
    const c = colors[i % colors.length];
    const angle = (i / 24) * 360;
    const dist = 60 + Math.random() * 80;
    const x = Math.cos((angle * Math.PI) / 180) * dist;
    const y = Math.sin((angle * Math.PI) / 180) * dist;
    const size = 6 + Math.random() * 8;
    const delay = Math.random() * 0.2;
    return (
      <div
        key={i}
        style={{
          position: 'absolute',
          left: '50%', top: '50%',
          width: size, height: size,
          borderRadius: i % 3 === 0 ? '50%' : i % 3 === 1 ? '2px' : '0',
          background: c,
          transform: `translate(${x}px, ${y}px) rotate(${angle}deg)`,
          opacity: 0,
          animation: `mqConfetti .8s ${delay}s ease-out forwards`,
        }}
      />
    );
  });
  return <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 50 }}>{pieces}</div>;
};

/* ═══════════════════════════════════════════════════════════════════════════
   STAR REWARD (shown on correct answer)
   ═══════════════════════════════════════════════════════════════════════════ */
const StarReward = ({ show }) => {
  if (!show) return null;
  return (
    <div className="flex justify-center my-4" style={{ animation: 'mqStarSpin .6s ease-out both' }}>
      <div className="flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-black text-xl shadow-lg">
        <span className="text-3xl">+1</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   AUDIO BUTTON
   ═══════════════════════════════════════════════════════════════════════════ */
const AudioBtn = ({ url, label = 'Nghe' }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  if (!url) return null;
  const toggle = () => {
    if (!ref.current) return;
    playing ? ref.current.pause() : ref.current.play();
  };
  return (
    <button
      onClick={toggle}
      className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-all shadow-sm
        ${playing ? 'bg-amber-500 text-white scale-105' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
    >
      <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>
        {playing ? 'pause_circle' : 'play_circle'}
      </span>
      {label}
      <audio
        ref={ref}
        src={url}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </button>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MASCOT BUBBLE (story narration for kids)
   ═══════════════════════════════════════════════════════════════════════════ */
const MascotBubble = ({ text, emoji = '🦊', mood = 'happy' }) => {
  if (!text) return null;
  const bg = mood === 'happy' ? 'bg-sky-50 border-sky-200' : mood === 'encourage' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200';
  return (
    <div className={`mq-fadeup flex items-start gap-3 p-4 rounded-2xl border-2 ${bg} mb-4`}>
      <div className="text-3xl mq-float flex-shrink-0">{emoji}</div>
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-700 leading-relaxed">{text}</p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   QUIZ TYPE RENDERERS
   ═══════════════════════════════════════════════════════════════════════════ */

// ── TOUCH_SELECT ─────────────────────────────────────────────────────────
const TouchSelectQuiz = ({ quiz, onAnswer, disabled, result }) => {
  const [selected, setSelected] = useState(null);
  const ctx = quiz.context || {};
  const q = quiz.question || {};
  const opts = quiz.options || [];
  const correctId = quiz.answer_and_feedback?.answer;

  const pick = (optId) => {
    if (disabled) return;
    setSelected(optId);
    onAnswer(optId);
  };

  return (
    <div className="mq-fadeup space-y-4">
      <MascotBubble text={ctx.story_text} emoji="🦉" />

      {ctx.image_url && (
        <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-white">
          <img src={ctx.image_url} alt="scene" className="w-full h-auto" />
        </div>
      )}

      <div className="flex gap-2 flex-wrap justify-center">
        <AudioBtn url={ctx.audio_url} label="Nghe chuyện" />
        <AudioBtn url={q.audio_url} label="Nghe hỏi" />
      </div>

      <div className="text-center py-2">
        <p className="text-2xl font-black text-slate-800">{q.text}</p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {opts.map((opt) => {
          let cls = 'mq-option flex items-center gap-4 p-4 rounded-2xl border-3 border-slate-200 bg-white';
          if (disabled) cls += ' mq-disabled';
          if (result && selected === opt.id) cls += result.correct ? ' mq-correct' : ' mq-wrong';
          if (result && !result.correct && opt.id === correctId) cls += ' mq-reveal-correct';
          return (
            <button key={opt.id} className={cls} onClick={() => pick(opt.id)} disabled={disabled}
              style={{ border: '3px solid', borderColor: selected === opt.id && !result ? '#818cf8' : '#e2e8f0' }}>
              <span className="w-12 h-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl font-black flex-shrink-0">
                {opt.id.replace('opt_', '')}
              </span>
              <span className="text-lg font-bold text-slate-700">{opt.text}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── SPELLING_TYPING (Letter Keyboard) ────────────────────────────────────
/**
 * Build a shuffled pool of letter tiles:
 * - All letters from the correct answer
 * - ~35% extra distractor letters (random a-z)
 * Each tile has a unique key so the same letter can appear multiple times.
 */
function buildLetterPool(answer) {
  const answerLetters = answer.toLowerCase().split('');
  const extraCount = Math.max(2, Math.ceil(answerLetters.length * 0.35));
  const alpha = 'abcdefghijklmnopqrstuvwxyz';
  const extras = [];
  for (let i = 0; i < extraCount; i++) {
    extras.push(alpha[Math.floor(Math.random() * 26)]);
  }
  const pool = [...answerLetters, ...extras].map((ch, i) => ({ ch, key: `${ch}-${i}` }));
  // Fisher-Yates shuffle
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool;
}

const SpellingTypingQuiz = ({ quiz, onAnswer, disabled, result }) => {
  const ctx = quiz.context || {};
  const q = quiz.question || {};
  const correctAns = quiz.answer_and_feedback?.answer || '';

  // Build letter pool once
  const [pool] = useState(() => buildLetterPool(correctAns));
  const [picked, setPicked] = useState([]);       // [{ch, key}, ...] in answer box
  const [usedKeys, setUsedKeys] = useState(new Set()); // keys already used
  const [flyingKey, setFlyingKey] = useState(null);     // key currently animating

  const typed = picked.map((p) => p.ch).join('');
  const isCorrect = result?.correct;

  const pickLetter = (tile) => {
    if (disabled || usedKeys.has(tile.key)) return;
    // Trigger fly animation
    setFlyingKey(tile.key);
    setTimeout(() => {
      setPicked((prev) => [...prev, tile]);
      setUsedKeys((prev) => new Set(prev).add(tile.key));
      setFlyingKey(null);
    }, 250);
  };

  const removeLast = () => {
    if (disabled || picked.length === 0) return;
    const last = picked[picked.length - 1];
    setPicked((prev) => prev.slice(0, -1));
    setUsedKeys((prev) => { const s = new Set(prev); s.delete(last.key); return s; });
  };

  const submit = () => {
    if (disabled || !typed) return;
    onAnswer(typed);
  };

  // Color each slot: green if correct position, red if wrong (after submit)
  const getSlotColor = (idx) => {
    if (!result) return 'border-indigo-300 bg-white';
    const correctLower = correctAns.toLowerCase();
    if (idx < correctLower.length && picked[idx]?.ch === correctLower[idx]) {
      return 'border-green-400 bg-green-50';
    }
    return 'border-red-400 bg-red-50';
  };

  return (
    <div className="mq-fadeup space-y-4">
      <MascotBubble text={ctx.story_text} emoji="🐻" mood="encourage" />

      {ctx.image_url && (
        <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-white">
          <img src={ctx.image_url} alt="hint" className="w-full h-auto" />
        </div>
      )}

      <div className="flex gap-2 flex-wrap justify-center">
        <AudioBtn url={ctx.audio_url} label="Nghe chuyện" />
        <AudioBtn url={q.audio_url} label="Nghe hỏi" />
      </div>

      {/* Sentence with blank */}
      <div className="bg-indigo-50 rounded-2xl p-5 border-2 border-indigo-100">
        <div className="flex items-center justify-center gap-2 flex-wrap text-xl font-bold text-slate-800">
          {q.text_before_blank && <span>{q.text_before_blank}</span>}

          {/* Answer slots */}
          <div className="flex items-center gap-1 min-h-[48px] px-2 py-1 bg-white rounded-xl border-2 border-dashed border-indigo-300 relative">
            {picked.length === 0 && (
              <span className="text-indigo-300 text-lg px-2">tap letters below</span>
            )}
            {picked.map((tile, idx) => (
              <span
                key={tile.key}
                className={`mq-pop inline-flex items-center justify-center w-9 h-10 rounded-lg border-2 text-lg font-black uppercase ${getSlotColor(idx)}`}
              >
                {tile.ch}
              </span>
            ))}
            {/* Backspace */}
            {picked.length > 0 && !disabled && (
              <button
                onClick={removeLast}
                className="ml-1 w-8 h-8 rounded-lg bg-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-300 transition-colors"
              >
                <span className="material-symbols-outlined text-lg">backspace</span>
              </button>
            )}
          </div>

          {q.text_after_blank && <span>{q.text_after_blank}</span>}
        </div>

        {/* Show correct answer on wrong */}
        {result && !isCorrect && (
          <div className="mt-3 text-center mq-pop">
            <span className="text-sm font-bold text-slate-400">Correct: </span>
            <span className="text-lg font-black text-green-600 uppercase tracking-widest">{correctAns}</span>
          </div>
        )}
      </div>

      {/* Letter keyboard */}
      <div className="bg-white rounded-2xl p-4 border-2 border-slate-200 shadow-sm">
        <p className="text-xs font-bold text-slate-400 uppercase text-center mb-3">Tap the letters</p>
        <div className="flex flex-wrap justify-center gap-2">
          {pool.map((tile) => {
            const used = usedKeys.has(tile.key);
            const flying = flyingKey === tile.key;
            return (
              <button
                key={tile.key}
                onClick={() => pickLetter(tile)}
                disabled={disabled || used}
                className={`
                  w-11 h-12 rounded-xl text-lg font-black uppercase transition-all
                  ${flying ? 'mq-pop scale-0 opacity-0' : ''}
                  ${used
                    ? 'bg-slate-100 text-slate-300 border-2 border-slate-100 scale-90'
                    : 'bg-gradient-to-b from-white to-slate-50 text-slate-700 border-2 border-slate-300 shadow-sm hover:border-indigo-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0 active:shadow-none'
                  }
                `}
              >
                {tile.ch}
              </button>
            );
          })}
        </div>
      </div>

      {/* Submit button */}
      {!result && (
        <button
          onClick={submit}
          disabled={disabled || !typed}
          className="w-full py-4 rounded-2xl text-white text-xl font-black shadow-lg transition-all active:scale-95"
          style={{
            background: typed ? 'linear-gradient(135deg, #818cf8, #6366f1)' : '#cbd5e1',
            border: 'none', cursor: typed ? 'pointer' : 'default',
          }}
        >
          Kiểm tra
        </button>
      )}
    </div>
  );
};

// ── MULTIPLE_CHOICE_IMAGE ────────────────────────────────────────────────
const MultipleChoiceImageQuiz = ({ quiz, onAnswer, disabled, result }) => {
  const [selected, setSelected] = useState(null);
  const ctx = quiz.context || {};
  const q = quiz.question || {};
  const opts = quiz.options || [];
  const correctId = quiz.answer_and_feedback?.answer;

  const pick = (optId) => {
    if (disabled) return;
    setSelected(optId);
    onAnswer(optId);
  };

  return (
    <div className="mq-fadeup space-y-4">
      <MascotBubble text={ctx.story_text} emoji="🐰" />

      {ctx.image_url && (
        <div className="rounded-2xl overflow-hidden shadow-md border-4 border-white">
          <img src={ctx.image_url} alt="context" className="w-full h-auto" />
        </div>
      )}

      <div className="flex gap-2 flex-wrap justify-center">
        <AudioBtn url={ctx.audio_url} label="Nghe chuyện" />
        <AudioBtn url={q.audio_url} label="Nghe hỏi" />
      </div>

      <div className="text-center py-2">
        <p className="text-xl font-black text-slate-800">{q.text}</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {opts.map((opt) => {
          const isSel = selected === opt.id;
          const isCorr = result && opt.id === correctId;
          const isWrong = result && isSel && !result.correct;
          let ring = isSel && !result ? 'ring-4 ring-indigo-400' : '';
          if (isCorr) ring = 'ring-4 ring-green-400';
          if (isWrong) ring = 'ring-4 ring-red-400';
          return (
            <button
              key={opt.id}
              className={`mq-option rounded-2xl overflow-hidden bg-white border-2 border-slate-200 ${ring} ${disabled ? 'mq-disabled' : ''} ${isCorr ? 'mq-correct' : ''} ${isWrong ? 'mq-shake' : ''}`}
              onClick={() => pick(opt.id)}
              disabled={disabled}
            >
              {opt.image_url && (
                <img src={opt.image_url} alt={opt.text} className="w-full h-28 object-cover" />
              )}
              <div className="p-2 text-center">
                <span className="text-sm font-bold text-slate-700">{opt.text}</span>
              </div>
              {isCorr && (
                <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 rounded-2xl">
                  <span className="text-4xl">&#10003;</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── MULTIPLE_CHOICE_TEXT ──────────────────────────────────────────────────
const MultipleChoiceTextQuiz = ({ quiz, onAnswer, disabled, result }) => {
  const [selected, setSelected] = useState(null);
  const ctx = quiz.context || {};
  const q = quiz.question || {};
  const opts = quiz.options || [];
  const correctId = quiz.answer_and_feedback?.answer;

  const pick = (optId) => {
    if (disabled) return;
    setSelected(optId);
    onAnswer(optId);
  };

  const letters = ['A', 'B', 'C', 'D', 'E'];

  return (
    <div className="mq-fadeup space-y-4">
      <MascotBubble text={ctx.story_text} emoji="🦊" />

      {ctx.image_url && (
        <div className="rounded-2xl overflow-hidden shadow-md border-4 border-white">
          <img src={ctx.image_url} alt="context" className="w-full h-auto" />
        </div>
      )}

      <div className="flex gap-2 flex-wrap justify-center">
        <AudioBtn url={ctx.audio_url} label="Nghe chuyện" />
        <AudioBtn url={q.audio_url} label="Nghe hỏi" />
      </div>

      <div className="text-center py-2">
        <p className="text-2xl font-black text-slate-800">{q.text}</p>
      </div>

      <div className="space-y-3">
        {opts.map((opt, i) => {
          const isSel = selected === opt.id;
          const isCorr = result && opt.id === correctId;
          const isWrong = result && isSel && !result.correct;
          let border = isSel && !result ? '#818cf8' : '#e2e8f0';
          let bg = 'white';
          let extraCls = '';
          if (isCorr) { border = '#22c55e'; bg = '#f0fdf4'; extraCls = 'mq-correct'; }
          if (isWrong) { border = '#ef4444'; bg = '#fef2f2'; extraCls = 'mq-wrong'; }
          return (
            <button
              key={opt.id}
              className={`mq-option w-full flex items-center gap-4 p-4 rounded-2xl ${extraCls} ${disabled ? 'mq-disabled' : ''}`}
              onClick={() => pick(opt.id)}
              disabled={disabled}
              style={{ border: `3px solid ${border}`, background: bg }}
            >
              <span className={`w-11 h-11 rounded-full flex items-center justify-center text-lg font-black flex-shrink-0
                ${isCorr ? 'bg-green-500 text-white' : isWrong ? 'bg-red-500 text-white' : isSel && !result ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {letters[i] || opt.id}
              </span>
              <span className="text-lg font-bold text-slate-700 text-left">{opt.text}</span>
              {isCorr && <span className="ml-auto text-2xl">&#10003;</span>}
              {isWrong && <span className="ml-auto text-2xl">&#10007;</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── STORY_CONSTRUCTION ───────────────────────────────────────────────────
const StoryConstructionQuiz = ({ quiz, onAnswer, disabled, result }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const ctx = quiz.context || {};
  const q = quiz.question || {};
  const opts = quiz.options || [];
  const correctSeq = quiz.answer_and_feedback?.answer_sequence || [];

  const toggleOpt = (optId) => {
    if (disabled) return;
    setSelectedIds((prev) => {
      if (prev.includes(optId)) return prev.filter((id) => id !== optId);
      if (prev.length >= 3) return prev; // max 3
      return [...prev, optId];
    });
  };

  const submitSeq = () => {
    if (disabled || selectedIds.length < 3) return;
    onAnswer(selectedIds);
  };

  const isSeqCorrect = result?.correct;

  return (
    <div className="mq-fadeup space-y-4">
      <MascotBubble
        text="Look at the pictures and build the story! Pick 3 sentences in the right order."
        emoji="🎨"
        mood="encourage"
      />

      {/* Story images */}
      {ctx.image_urls && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {ctx.image_urls.map((url, i) => (
            <div key={i} className="flex-shrink-0 relative">
              <img src={url} alt={`scene ${i + 1}`} className="w-40 h-28 rounded-xl object-cover shadow-md border-3 border-white" />
              <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-sm font-black text-slate-700 shadow">
                {i + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-center py-1">
        <p className="text-lg font-black text-slate-800">{q.text}</p>
      </div>

      {/* Selected sequence */}
      {selectedIds.length > 0 && (
        <div className="bg-indigo-50 rounded-2xl p-4 border-2 border-indigo-100">
          <p className="text-xs font-bold text-indigo-400 uppercase mb-2">Your story order:</p>
          <div className="space-y-2">
            {selectedIds.map((id, i) => {
              const opt = opts.find((o) => o.id === id);
              const isCorrectPos = result && correctSeq[i] === id;
              const isWrongPos = result && correctSeq[i] !== id;
              return (
                <div
                  key={id}
                  className={`flex items-center gap-3 p-3 rounded-xl bg-white border-2 mq-slidein
                    ${isCorrectPos ? 'border-green-400 bg-green-50' : isWrongPos ? 'border-red-400 bg-red-50' : 'border-indigo-200'}`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0
                    ${isCorrectPos ? 'bg-green-500 text-white' : isWrongPos ? 'bg-red-500 text-white' : 'bg-indigo-500 text-white'}`}>
                    {i + 1}
                  </span>
                  <span className="text-sm font-semibold text-slate-700 flex-1">{opt?.text}</span>
                  {!disabled && (
                    <button onClick={() => toggleOpt(id)} className="text-slate-400 hover:text-red-500 text-lg font-bold">&#10007;</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Options to pick from */}
      <div className="space-y-2">
        {opts.map((opt) => {
          const picked = selectedIds.includes(opt.id);
          const isInCorrectSeq = result && correctSeq.includes(opt.id);
          return (
            <button
              key={opt.id}
              className={`mq-option w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left
                ${picked ? 'border-indigo-400 bg-indigo-50 opacity-50' : 'border-slate-200 bg-white'}
                ${disabled ? 'mq-disabled' : ''}
                ${result && isInCorrectSeq && !picked ? 'border-green-300 bg-green-50' : ''}`}
              onClick={() => toggleOpt(opt.id)}
              disabled={disabled || picked}
            >
              <span className="text-sm font-semibold text-slate-700">{opt.text}</span>
              {picked && <span className="ml-auto text-indigo-400 font-bold text-xs">#{selectedIds.indexOf(opt.id) + 1}</span>}
            </button>
          );
        })}
      </div>

      {!result && (
        <button
          onClick={submitSeq}
          disabled={disabled || selectedIds.length < 3}
          className="w-full py-4 rounded-2xl text-white text-xl font-black shadow-lg transition-all active:scale-95"
          style={{
            background: selectedIds.length >= 3 ? 'linear-gradient(135deg, #818cf8, #6366f1)' : '#cbd5e1',
            border: 'none', cursor: selectedIds.length >= 3 ? 'pointer' : 'default',
          }}
        >
          Check Story Order
        </button>
      )}

      {/* Show correct sequence on wrong */}
      {result && !result.correct && (
        <div className="bg-green-50 rounded-2xl p-4 border-2 border-green-200 mq-fadeup">
          <p className="text-xs font-bold text-green-600 uppercase mb-2">Correct order:</p>
          {correctSeq.map((id, i) => {
            const opt = opts.find((o) => o.id === id);
            return (
              <div key={id} className="flex items-center gap-3 py-1">
                <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-black">{i + 1}</span>
                <span className="text-sm font-semibold text-green-800">{opt?.text}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FEEDBACK OVERLAY (shown after answering)
   ═══════════════════════════════════════════════════════════════════════════ */
const FeedbackPanel = ({ correct, feedback, onNext, isLast }) => {
  const fb = feedback || {};
  return (
    <div className={`mq-pop rounded-2xl p-5 mt-4 border-2 relative overflow-hidden
      ${correct ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300' : 'bg-gradient-to-br from-red-50 to-orange-50 border-red-300'}`}>
      <ConfettiBurst show={correct} />
      <StarReward show={correct} />

      <div className="text-center mb-3">
        <span className="text-4xl">{correct ? '🎉' : '💪'}</span>
        <p className={`text-2xl font-black mt-1 ${correct ? 'text-green-700' : 'text-red-700'}`}>
          {correct ? 'Excellent!' : 'Not quite!'}
        </p>
      </div>

      {fb.en && (
        <p className="text-center text-slate-600 font-medium text-sm mb-1">{fb.en}</p>
      )}
      {fb.vi && (
        <p className="text-center text-slate-500 text-xs italic">{fb.vi}</p>
      )}

      <button
        onClick={onNext}
        className="mt-4 w-full py-3 rounded-2xl text-white text-lg font-black shadow-lg transition-all active:scale-95"
        style={{
          background: correct ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #f59e0b, #d97706)',
          border: 'none', cursor: 'pointer',
        }}
      >
        {isLast ? 'See Results' : 'Next Question →'}
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   RESULTS SCREEN (end of session)
   ═══════════════════════════════════════════════════════════════════════════ */
const ResultsScreen = ({ results, quizzes, onRestart, onClose }) => {
  const correct = results.filter((r) => r.correct).length;
  const total = results.length;
  const pct = Math.round((correct / total) * 100);
  const emoji = pct === 100 ? '🏆' : pct >= 60 ? '⭐' : '💪';
  const msg = pct === 100 ? 'Perfect Score!' : pct >= 60 ? 'Great Job!' : 'Keep Practicing!';

  return (
    <div className="mq-pop text-center space-y-6 py-8">
      <div className="text-7xl">{emoji}</div>
      <h2 className="text-3xl font-black text-slate-800">{msg}</h2>
      <div className="flex justify-center gap-4">
        <div className="bg-green-50 rounded-2xl p-4 border-2 border-green-200 min-w-[100px]">
          <p className="text-3xl font-black text-green-600">{correct}</p>
          <p className="text-xs font-bold text-green-500 uppercase">Correct</p>
        </div>
        <div className="bg-slate-50 rounded-2xl p-4 border-2 border-slate-200 min-w-[100px]">
          <p className="text-3xl font-black text-slate-600">{total}</p>
          <p className="text-xs font-bold text-slate-400 uppercase">Total</p>
        </div>
        <div className="bg-amber-50 rounded-2xl p-4 border-2 border-amber-200 min-w-[100px]">
          <p className="text-3xl font-black text-amber-600">{pct}%</p>
          <p className="text-xs font-bold text-amber-500 uppercase">Score</p>
        </div>
      </div>

      {/* Per-quiz breakdown */}
      <div className="space-y-2 text-left max-w-md mx-auto">
        {results.map((r, i) => (
          <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${r.correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <span className="text-xl">{r.correct ? '✅' : '❌'}</span>
            <span className="text-sm font-bold text-slate-700 flex-1">{quizzes[i]?.quiz_type}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-3 justify-center pt-2">
        <button
          onClick={onRestart}
          className="px-6 py-3 rounded-2xl bg-indigo-500 text-white font-black text-lg shadow-lg transition-all active:scale-95"
        >
          Try Again
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-2xl bg-slate-200 text-slate-700 font-black text-lg transition-all active:scale-95"
        >
          Close
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   QUIZ TYPE → LABEL & COLOR mapping
   ═══════════════════════════════════════════════════════════════════════════ */
const TYPE_META = {
  TOUCH_SELECT:          { label: 'Touch & Select',    color: '#f59e0b', emoji: '👆' },
  SPELLING_TYPING:       { label: 'Spell It Out',      color: '#8b5cf6', emoji: '✍️' },
  MULTIPLE_CHOICE_IMAGE: { label: 'Pick the Picture',  color: '#06b6d4', emoji: '🖼️' },
  MULTIPLE_CHOICE_TEXT:  { label: 'Choose the Answer',  color: '#3b82f6', emoji: '📝' },
  STORY_CONSTRUCTION:    { label: 'Build the Story',   color: '#ec4899', emoji: '📖' },
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT — MockupQuizSession
   ═══════════════════════════════════════════════════════════════════════════ */
const MockupQuizSession = ({ quizzes, open, onClose, onSubmit }) => {
  injectStyles();

  const [currentIdx, setCurrentIdx] = useState(0);
  const [results, setResults] = useState([]); // [{correct, feedback}]
  const [currentResult, setCurrentResult] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const contentRef = useRef(null);

  const quiz = quizzes[currentIdx];
  const total = quizzes.length;
  const meta = TYPE_META[quiz?.quiz_type] || { label: 'Quiz', color: '#6366f1', emoji: '❓' };

  // Reset on open
  useEffect(() => {
    if (open) {
      setCurrentIdx(0);
      setResults([]);
      setCurrentResult(null);
      setShowResults(false);
    }
  }, [open]);

  // Scroll to top on quiz change
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentIdx]);

  const handleAnswer = useCallback((answer) => {
    if (currentResult) return;

    const fb = quiz.answer_and_feedback || {};
    let correct = false;

    if (Array.isArray(answer)) {
      // STORY_CONSTRUCTION: compare sequences
      const correctSeq = fb.answer_sequence || [];
      correct = answer.length === correctSeq.length && answer.every((id, i) => id === correctSeq[i]);
    } else if (fb.answer) {
      correct = String(answer).toLowerCase() === String(fb.answer).toLowerCase();
    }

    const feedbackObj = correct ? fb.correct_feedback : fb.incorrect_feedback;
    setCurrentResult({ correct, feedback: feedbackObj });

    // Optional: notify parent (e.g. to POST backend — triggers quest updates, mastery)
    if (onSubmit) {
      try { onSubmit(quiz, answer, correct); } catch (e) { /* fire-and-forget */ }
    }
  }, [quiz, currentResult, onSubmit]);

  const goNext = () => {
    setResults((prev) => [...prev, currentResult]);
    setCurrentResult(null);

    if (currentIdx + 1 >= total) {
      setShowResults(true);
    } else {
      setCurrentIdx((prev) => prev + 1);
    }
  };

  const restart = () => {
    setCurrentIdx(0);
    setResults([]);
    setCurrentResult(null);
    setShowResults(false);
  };

  if (!open || !quizzes.length) return null;

  /* Quiz type → renderer */
  const renderQuiz = () => {
    const props = { quiz, onAnswer: handleAnswer, disabled: !!currentResult, result: currentResult };
    switch (quiz.quiz_type) {
      case 'TOUCH_SELECT':          return <TouchSelectQuiz {...props} />;
      case 'SPELLING_TYPING':       return <SpellingTypingQuiz {...props} />;
      case 'MULTIPLE_CHOICE_IMAGE': return <MultipleChoiceImageQuiz {...props} />;
      case 'MULTIPLE_CHOICE_TEXT':  return <MultipleChoiceTextQuiz {...props} />;
      case 'STORY_CONSTRUCTION':    return <StoryConstructionQuiz {...props} />;
      default: return <div className="p-8 text-center text-slate-400">Unsupported quiz type: {quiz.quiz_type}</div>;
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* ── TOP BAR ─────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-4 pt-4 pb-2">
        <div className="flex items-center justify-between mb-3">
          <button onClick={onClose} className="flex items-center gap-1 text-slate-400 hover:text-slate-600 font-bold text-sm transition-colors">
            <span className="material-symbols-outlined text-xl">close</span>
            Exit
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xl">{meta.emoji}</span>
            <span className="text-sm font-black uppercase" style={{ color: meta.color }}>{meta.label}</span>
          </div>
          <span className="text-sm font-bold text-slate-400">
            {currentIdx + 1} / {total}
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="mq-progress-fill h-full rounded-full"
            style={{ width: `${((currentIdx + (currentResult ? 1 : 0)) / total) * 100}%`, background: meta.color }}
          />
        </div>
      </div>

      {/* ── CONTENT ─────────────────────────────────────────── */}
      <div ref={contentRef} className="flex-1 overflow-y-auto px-4 py-4" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-lg mx-auto">
          {showResults ? (
            <ResultsScreen results={[...results]} quizzes={quizzes} onRestart={restart} onClose={onClose} />
          ) : (
            <>
              {renderQuiz()}
              {currentResult && (
                <FeedbackPanel
                  correct={currentResult.correct}
                  feedback={currentResult.feedback}
                  onNext={goNext}
                  isLast={currentIdx + 1 >= total}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MockupQuizSession;
